/**
 * UI Component Visibility System — Type Definitions
 *
 * Three classification axes control which call-page elements are shown:
 *   - CallImportance:     higher = more critical to handling the call
 *   - TechLevel:          higher = more technical / power-user
 *   - InvestigativeValue: higher = more useful for investigation
 *
 * Numeric values allow simple threshold comparison.
 */

// ── Enums (ascending numeric order) ──────────────────────────────────────────

export enum CallImportance {
  DEV = 0,
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  CRITICAL = 4,
}

export enum TechLevel {
  BASIC = 0,
  INTERMEDIATE = 1,
  ADVANCED = 2,
  EXPERT = 3,
  DEV = 4,
}

export enum InvestigativeValue {
  NONE = 0,
  CONTEXTUAL = 1,
  SPECIALIST = 2,
  ENHANCED = 3,
  CORE = 4,
}

// ── Registry entry ───────────────────────────────────────────────────────────

export interface ComponentVisibilityEntry {
  importance: CallImportance;
  techLevel: TechLevel;
  investigativeValue: InvestigativeValue;
  /** If true this component is never hidden regardless of slider position */
  critical?: boolean;
  /** Human-readable label shown in the settings override list */
  label?: string;
  /** Grouping key for the settings override list (header, controls, actions, …) */
  group?: string;
}

// ── User preferences ─────────────────────────────────────────────────────────

export type VisibilityPresetName =
  | 'essentials'
  | 'standard'
  | 'advanced'
  | 'full'
  | 'training'
  | 'developer'
  | 'custom';

export interface VisibilityPreferences {
  /** Active preset, or 'custom' when sliders are manually adjusted */
  activePreset: VisibilityPresetName;
  /** Minimum call importance to show (0-4).  component.importance >= this */
  importanceThreshold: number;
  /** Maximum tech level to show (0-4).       component.techLevel <= this */
  techLevelThreshold: number;
  /** Minimum investigative value to show (0-4). component.investigativeValue >= this */
  investigativeThreshold: number;
  /** Per-component overrides — true = force-show, false = force-hide, absent = use threshold */
  overrides: Record<string, boolean>;
}

// ── Slider metadata ──────────────────────────────────────────────────────────

export const IMPORTANCE_LABELS: Record<number, string> = {
  [CallImportance.DEV]: 'Dev',
  [CallImportance.LOW]: 'Low',
  [CallImportance.MEDIUM]: 'Medium',
  [CallImportance.HIGH]: 'High',
  [CallImportance.CRITICAL]: 'Critical',
};

export const TECH_LEVEL_LABELS: Record<number, string> = {
  [TechLevel.BASIC]: 'Basic',
  [TechLevel.INTERMEDIATE]: 'Intermediate',
  [TechLevel.ADVANCED]: 'Advanced',
  [TechLevel.EXPERT]: 'Expert',
  [TechLevel.DEV]: 'Dev',
};

export const INVESTIGATIVE_LABELS: Record<number, string> = {
  [InvestigativeValue.NONE]: 'None',
  [InvestigativeValue.CONTEXTUAL]: 'Contextual',
  [InvestigativeValue.SPECIALIST]: 'Specialist',
  [InvestigativeValue.ENHANCED]: 'Enhanced',
  [InvestigativeValue.CORE]: 'Core',
};
